import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from '@/hooks/use-toast';
import { showApiError } from '@/lib/errors';
import { ApiError } from '@/lib/api';
import { appendOrganizationIdToUrl, getOrganizationId, withOrganizationId } from '@/lib/api/organization';
import { getAiAnalyticsBaseUrl } from '@/lib/api/aiAnalyticsUrl';

// Use AI_ANALYTICS_BASE_URL for livechat API as it matches the backend URL pattern
const LIVE_CHAT_BASE_URL = getAiAnalyticsBaseUrl('api');

export interface LiveChatChannel {
  id: string;
  business_account_id: string;
  website_url: string;
  channel_name: string;
  script_id: string;
  created_at: string;
  updated_at?: string;
  phone_number?: string;
  email?: string;
  custom_message?: string;
  logo?: string;
  connected_agent?: string;
}

export interface CreateChannelData {
  website_url?: string;
  business_account_id: string;
  channel_name: string;
  phone_number?: string;
  email?: string;
  custom_message?: string;
  logo?: string;
  connected_agent?: string;
}

export interface UpdateWidgetData {
  phone_number?: string;
  custom_message?: string;
  button_color_from?: string;
  button_color_to?: string;
  header_color_from?: string;
  header_color_to?: string;
  logo?: string;
}

export interface UpdateWidgetResponse {
  success: boolean;
  message?: string;
  widget?: LiveChatScript;
}

export interface DeleteWidgetResponse {
  success: boolean;
  message?: string;
}

export interface GenerateWidgetResponse {
  business_account_id: string;
  website_url: string;
  script_id: string;
  script: {
    script_content: string;
    preview_url: string;
    script_embed: string;
    customization_summary: string;
  };
  analysis: {
    primary_colors: string[];
    secondary_colors: string[];
    design_style: string;
    brand_tone: string;
  };
  token_usage: {
    model: string;
    input_tokens: number;
    output_tokens: number;
    total_tokens: number;
    input_cost: number;
    output_cost: number;
    total_cost: number;
    duration_seconds: number;
  };
  generated_at: string;
  success: boolean;
}

const apiClient = {
  async get<T>(endpoint: string): Promise<{ data: T | null; error: string | null }> {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${LIVE_CHAT_BASE_URL}${buildLiveChatEndpoint(endpoint)}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: `HTTP ${response.status}` }));
        return { data: null, error: errorData.error || `HTTP ${response.status}` };
      }

      const data = await response.json();
      return { data: data.data || data, error: null };
    } catch (error) {
      return { data: null, error: error instanceof Error ? error.message : 'Network error' };
    }
  },

  async post<T>(endpoint: string, body: any): Promise<{ data: T | null; error: string | null }> {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${LIVE_CHAT_BASE_URL}${buildLiveChatEndpoint(endpoint)}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(withOrganizationId(body || {})),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: `HTTP ${response.status}` }));
        return { data: null, error: errorData.error || `HTTP ${response.status}` };
      }

      const data = await response.json();
      return { data: data.data || data, error: null };
    } catch (error) {
      return { data: null, error: error instanceof Error ? error.message : 'Network error' };
    }
  },

  async put<T>(endpoint: string, body: any): Promise<{ data: T | null; error: string | null }> {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${LIVE_CHAT_BASE_URL}${buildLiveChatEndpoint(endpoint)}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(withOrganizationId(body || {})),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: `HTTP ${response.status}` }));
        return { data: null, error: errorData.error || `HTTP ${response.status}` };
      }

      const data = await response.json();
      return { data: data.data || data, error: null };
    } catch (error) {
      return { data: null, error: error instanceof Error ? error.message : 'Network error' };
    }
  },

  async delete<T>(endpoint: string): Promise<{ data: T | null; error: string | null }> {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${LIVE_CHAT_BASE_URL}${buildLiveChatEndpoint(endpoint)}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: `HTTP ${response.status}` }));
        return { data: null, error: errorData.error || `HTTP ${response.status}` };
      }

      const data = await response.json();
      return { data: data.data || data, error: null };
    } catch (error) {
      return { data: null, error: error instanceof Error ? error.message : 'Network error' };
    }
  },
};

export function useLiveChatChannels(businessAccountId?: string) {
  return useQuery({
    queryKey: ['live-chat-channels', businessAccountId],
    queryFn: async () => {
      if (!businessAccountId) return [];

      const result = await apiClient.get<LiveChatChannel[]>(`/livechat/channels?business_account_id=${businessAccountId}`);
      // If channels endpoint doesn't exist yet, return empty array
      if (result.error && result.error.includes('404')) {
        return [];
      }
      
      if (result.error) {
        throw ApiError.fromResult(result);
      }

      return result.data || [];
    },
    enabled: !!businessAccountId,
    staleTime: 30000, // 30 seconds
  });
}

export function useCreateLiveChatChannel() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateChannelData): Promise<GenerateWidgetResponse> => {
      const result = await apiClient.post<GenerateWidgetResponse>('/livechat/generate', data);
      
      if (result.error) {
        throw ApiError.fromResult(result);
      }

      if (!result.data) {
        throw new Error('No data returned from API');
      }

      return result.data;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['live-chat-scripts', variables.business_account_id] });
    },
    onError: (error: Error) => {
      // Error handling is done in the component
    },
  });
}

export function useLiveChatChannel(channelId?: string) {
  return useQuery({
    queryKey: ['live-chat-channel', channelId],
    queryFn: async () => {
      if (!channelId) return null;

      const result = await apiClient.get<LiveChatChannel>(`/livechat/channels/${channelId}`);
      // If channel endpoint doesn't exist yet, return null
      if (result.error && result.error.includes('404')) {
        return null;
      }
      
      if (result.error) {
        throw ApiError.fromResult(result);
      }

      return result.data;
    },
    enabled: !!channelId,
  });
}

export interface LiveChatScript {
  id: string;
  business_account_id: string;
  website_url?: string;
  script_content?: string;
  embed_script?: string; // The actual embed script tag (root level)
  script_embed?: string; // Alternative field name (root level)
  preview_url?: string;
  embed_token?: string;
  channel_name?: string;
  display_name?: string; // Display name from API response
  connected_agent?: string | null;
  logo?: string | null;
  phone_number?: string;
  email?: string;
  custom_message?: string;
  analysis_data?: string; // JSON string
  token_usage_data?: string; // JSON string
  customization_summary?: string;
  button_color_from?: string; // Button color from API response
  button_color_to?: string; // Button color to API response
  header_color_from?: string; // Header color from API response
  header_color_to?: string; // Header color to API response
  created_at: string;
  updated_at?: string;
  // Nested script object (from generate endpoint or some GET responses)
  script?: {
    script_content?: string;
    preview_url?: string;
    script_embed?: string;
    embed_script?: string; // The actual embed script tag
    customization_summary?: string;
    button_color_from?: string; // Button color from API response (nested)
    button_color_to?: string; // Button color to API response (nested)
    header_color_from?: string; // Header color from API response (nested)
    header_color_to?: string; // Header color to API response (nested)
  };
}

export interface LiveChatScriptsResponse {
  business_account_id: string;
  scripts: LiveChatScript[];
  count: number;
  success: boolean;
}

type LiveChatScriptsLike =
  | LiveChatScriptsResponse
  | { data?: LiveChatScriptsResponse | LiveChatScript[]; scripts?: LiveChatScript[] }
  | LiveChatScript[];

const getStoredOrganizationId = (): string | undefined => getOrganizationId() || undefined;

const buildLiveChatEndpoint = (endpoint: string) => {
  if (endpoint.includes('/livechat/organization/')) {
    return endpoint;
  }
  return appendOrganizationIdToUrl(endpoint);
};

const normalizeScripts = (payload: LiveChatScriptsLike | null | undefined): LiveChatScript[] => {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;
  if (Array.isArray((payload as any).scripts)) return (payload as any).scripts;
  if (Array.isArray((payload as any).data)) return (payload as any).data;
  if (Array.isArray((payload as any).data?.scripts)) return (payload as any).data.scripts;
  return [];
};

export function useLiveChatScripts(businessAccountId?: string, organizationId?: string) {
  return useQuery({
    queryKey: ['live-chat-scripts', businessAccountId, organizationId],
    queryFn: async () => {
      const resolvedOrganizationId = organizationId || getStoredOrganizationId();

      if (businessAccountId) {
        const result = await apiClient.get<LiveChatScriptsLike>(`/livechat/business/${businessAccountId}/scripts`);
        
        if (!result.error) {
          return normalizeScripts(result.data);
        }
      }

      if (!resolvedOrganizationId) return [];

      const result = await apiClient.get<LiveChatScriptsLike>(
        `/livechat/organization/${encodeURIComponent(resolvedOrganizationId)}/scripts`
      );
      
      if (result.error) {
        throw ApiError.fromResult(result);
      }

      return normalizeScripts(result.data);
    },
    enabled: !!businessAccountId || !!organizationId || !!getStoredOrganizationId(),
    staleTime: 30000, // 30 seconds
  });
}

export function useLiveChatScript(scriptId?: string) {
  return useQuery({
    queryKey: ['live-chat-script', scriptId],
    queryFn: async () => {
      if (!scriptId) return null;

      const result = await apiClient.get<LiveChatScript>(`/livechat/script/${scriptId}`);
      
      if (result.error) {
        if (result.error.includes('404')) {
          return null;
        }
        throw ApiError.fromResult(result);
      }

      return result.data;
    },
    enabled: !!scriptId,
  });
}


// Edit Widget Hook
export function useUpdateWidget() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ scriptId, data }: { scriptId: string; data: UpdateWidgetData }): Promise<UpdateWidgetResponse> => {
      const body: Record<string, string> = {};

      if (data.phone_number) body.phone_number = data.phone_number;
      if (data.custom_message) body.custom_message = data.custom_message;
      if (data.button_color_from) body.button_color_from = data.button_color_from;
      if (data.button_color_to) body.button_color_to = data.button_color_to;
      if (data.header_color_from) body.header_color_from = data.header_color_from;
      if (data.header_color_to) body.header_color_to = data.header_color_to;
      if (data.logo) body.logo = data.logo;

      const result = await apiClient.put<UpdateWidgetResponse>(`/livechat/widget/${scriptId}/update`, body);
      
      if (result.error) {
        throw ApiError.fromResult(result);
      }

      if (!result.data) {
        throw new Error('No data returned from API');
      }

      return result.data;
    },
    onSuccess: (data, variables) => {
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: ['live-chat-script', variables.scriptId] });
      queryClient.invalidateQueries({ queryKey: ['live-chat-scripts'] });
      
      toast({ variant: "success",
        title: 'Widget updated',
        description: 'Live chat widget has been updated successfully.',
      });
    },
    onError: (error: Error) => {
      showApiError(error, { fallback: { title: 'Update failed' } });
    },
  });
}

// Delete Widget Hook
export function useDeleteWidget() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (scriptId: string): Promise<DeleteWidgetResponse> => {
      const result = await apiClient.delete<DeleteWidgetResponse>(`/livechat/widget/${scriptId}/delete`);
      
      if (result.error) {
        throw ApiError.fromResult(result);
      }

      if (!result.data) {
        throw new Error('No data returned from API');
      }

      return result.data;
    },
    onSuccess: (data, scriptId) => {
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: ['live-chat-script', scriptId] });
      queryClient.invalidateQueries({ queryKey: ['live-chat-scripts'] });
      
      toast({ variant: "success",
        title: 'Widget deleted',
        description: 'Live chat widget has been deleted successfully.',
      });
    },
    onError: (error: Error) => {
      showApiError(error, { fallback: { title: 'Delete failed' } });
    },
  });
}
